import { 
    CallbackProperty, 
    Cartesian2, 
    Cartesian3, 
    Color, 
    ConstantProperty, 
    CornerType, 
    defined, 
    Entity, 
    HeightReference, 
    JulianDate, 
    PointGraphics, 
    PolylineDashMaterialProperty, 
    PolylineGraphics, 
    PropertyBag, 
    ScreenSpaceEventHandler, 
    ScreenSpaceEventType, 
    sampleTerrainMostDetailed, 
    Cartographic, 
    ColorMaterialProperty, 
    CorridorGraphics,
    CylinderGraphics, 
    ConstantPositionProperty,
    Viewer
} from "cesium";
import { mapToolsStore } from "../map-tools-store";
import { useDataSources } from "../cesium-datasources";
import { get } from "svelte/store";
import { cartesianToLatitudeLongitude, EntityTypes, getEntityPropertyBag, setSelectedEntity } from "../map-utils";
import { disableDefaultLeftClickHandler, removeAllScreenSpaceInputActions } from "../map-tools";
import { useCesiumViewer } from "../cesium-viewer";
import { v4 as uuidv4 } from "uuid";
import { OriginalCesiumInputActions } from "../cesium-original-input-actions";
import { getTrenchTopWidth } from "../trench-utils";
import { ENTITY_ID_PREFIX } from "$lib/utils/constants";
import { toast } from "$lib/components/organisms/toast/toast";
import { confirmModal } from "$lib/components/organisms/confirmModal/confirmModal";

// ----- Konfig for stabil hover -----
const ENTER_MS = 70;    // tid kandidat må være stabil før vi slår på highlight
const EXIT_MS  = 140;   // tid vi holder på highlight etter at pekeren glapp
const STICKY_PX = 3;    // pikselradius for "stickiness" (hindrer blink på små jitter)

// Hoverstate
let lastHighlighted: Entity | null = null;
let lastOriginal: Partial<CylinderGraphics> | null = null; // lagre originalstil
let rafId: number | null = null;
let lastMouse: Cartesian2 | null = null;

// Toolstate
let activeShapePoints: Cartesian3[] = [];
let activeShape: Entity | undefined;
let floatingPoint: Entity | undefined;
let finalEntity: Entity | undefined;


// Hjelp: hvilke entiteter skal vi "snappe" til ved hover?
function isAllowedTarget(entity: Entity): boolean {
  const idStr = String((entity as any).id ?? "");
  return idStr.includes(ENTITY_ID_PREFIX.POLE) ||
         idStr.includes("nettstasjon:") ||
         idStr.includes("kabelskap:") ||
         idStr.includes("sandbox_box:");
}

/**
 * Helper function: Returns true if entity is one of the endpoints (start or end) of the selection in which polyline being drawn
 * @param e Entity
 * @returns boolean
 */
const isEndpointEntity = (e: Entity | null | undefined) => {
    if (!e) return false;
    const { entityStart, entityEnd } = get(mapToolsStore).drawPolylineVolume;
    const startAnchor = entityStart;
    const endAnchor = entityEnd;

    if(!startAnchor && !endAnchor) return false;

    return (startAnchor && e === startAnchor) || (endAnchor && e === endAnchor);
}

// Hjelp: robust posisjonspick fra pixel → ellipsoide
const pickGlobePosition = (viewer: Viewer, pos: Cartesian2) => {
  const { scene } = viewer;
  if (scene.pickPositionSupported) {
    const p = scene.pickPosition(pos);
    if (defined(p)) return p as Cartesian3;
  }
  const ray = viewer.camera.getPickRay(pos);
  if (!ray) return undefined;
  const p = scene.globe?.pick(ray, scene);
  return p ?? undefined;
}


const doHoverWork = () => {
    const viewer = useCesiumViewer();
    const scene = viewer.scene;
    rafId = null;
    const pos = lastMouse;
    if (!pos) return;

    // Find first entity below cursor
    const picked = scene.pick(pos);
    const entity = (picked && picked.id instanceof Entity) ? (picked.id as Entity) : null;

    // Determine earthPosition for previewing the polyline
    let earthPosition: Cartesian3 | undefined;

    if (entity && isAllowedTarget(entity)) {
        earthPosition = entity.position?.getValue();
    } else {
        earthPosition = pickGlobePosition(viewer, pos);
    }

    let somethingChanged = false;

    if (earthPosition && defined(floatingPoint)) {
        // Update preview/floating point with new earth position.
        floatingPoint.position = new ConstantPositionProperty(earthPosition);
        
        if (activeShapePoints.length > 0) {
            activeShapePoints[activeShapePoints.length - 1] = earthPosition;
        } else {
            activeShapePoints.push(earthPosition);
        }
        somethingChanged = true;
    }
    
    /* USTABILT
    // Highlight: kun dersom hover treffer en gyldig sylinder-entitet som ikke allerede er markert. Bytt siste argument ut med isAllowedTarget(entity)
    const shouldHighlight = entity && !isEndpointEntity(entity) && entity.cylinder;
    if ((shouldHighlight ? entity! : null) !== lastHighlighted) {
        // rull tilbake forrige
        if (lastHighlighted && lastHighlighted.cylinder && lastOriginal) {
            const cyl = lastHighlighted.cylinder;
            cyl.material = lastOriginal.material;
            cyl.outline = lastOriginal.outline;
            cyl.outlineColor = lastOriginal.outlineColor;
            (cyl as any).outlineWidth = lastOriginal.outlineWidth; // width er property/number avh. av oppsett
        }
        lastHighlighted = null;
        lastOriginal = null;

        // påfør ny
        if (shouldHighlight) {
            const cyl = (entity as Entity).cylinder!;
            lastOriginal = {
                material: cyl.material,
                outline: (cyl.outline as any)?.getValue?.() ?? (cyl.outline as any),
                outlineColor: cyl.outlineColor,
                outlineWidth: (cyl as any).outlineWidth?.getValue?.() ?? (cyl as any).outlineWidth ?? 1
            };

            // Lett, tydelig highlight
            cyl.material = new ColorMaterialProperty(Color.YELLOW.withAlpha(0.6));
            cyl.outline = new ConstantProperty(true);
            cyl.outlineColor = new ColorMaterialProperty(Color.BLACK);
            (cyl as any).outlineWidth = new ConstantProperty(2);

            lastHighlighted = entity!;
        }
        somethingChanged = true;
    }*/

    if (somethingChanged) scene.requestRender();
}

const createPolylinePoint = (worldPosition: Cartesian3, uniqueIDForEntireLine: string) => {
    const {trenchEntityDatasource} = useDataSources();

    // First point needs to be an entity
    return trenchEntityDatasource.entities.add({
        id: `sandbox_polyline_point:${uuidv4()}`,
        position: worldPosition,
        properties: new PropertyBag({
            entityType: EntityTypes.SANDBOX_POLYLINE_PART,
            lineId: uniqueIDForEntireLine
        }),
        point: new PointGraphics({
            color: Color.WHITE,
            pixelSize: 5,
            heightReference: HeightReference.CLAMP_TO_GROUND
        })
    });
};


const drawTrench = async (polylinePositions: Cartesian3[]) => {
    const {trenchEntityDatasource} = useDataSources();
    const {entityStart, entityEnd} = get(mapToolsStore).drawPolylineVolume;
    if(!entityStart || !entityEnd){
        toast({
            type: 'error',
            description: 'Du må velge start- og endepunkt for grøften før du avslutter tegningen. Avslutter uten å lagre.',
            title: 'Start- og endepunkt mangler',
        })
        return;
    }
    const trenchId = `trenchPath:${uuidv4()}`
    const trenchSubdivisionId = `cablePath:${uuidv4()}`;

    const trenchData = {
        horizontalPadding: 0.1, // Klarering på
        topCover: [], // Unused at the moment
        refillZones: [{
            name: 'Standard',
            fillId: undefined,
            depth: 0.4,
            thermalResistivity: 2.5,
        }],
        cableZones: [{
            objects: [],
            margin: 0.15, // Klarering mellom kabel og gjenfyllingssone
            zone: {
                name: 'Kabelsone standard',
                fillId: undefined,
                thermalResistivity: 2.5,
            }
        }],
        foundationZones: [{
                name: 'Fundament standard',
                fillId: undefined,
                depth: 0.1,
                thermalResistivity: 2.5,
        }],
        trenchAngle: 60,
        startEntity: entityStart.id,
        endEntity: entityEnd.id,
        description: '',
        curve: undefined,
        steps: 0,
        trenchParentId: trenchId
    }

    const trenchTopWidth = getTrenchTopWidth(trenchData);

    // Create the trench path entity which is visible above with a RED color for easy identification. Inherited positions from polyline
    const trenchPath = trenchEntityDatasource.entities.add(new Entity({
        id: trenchId,
        corridor: new CorridorGraphics({
            positions: polylinePositions,
            cornerType: CornerType.MITERED,
            heightReference: HeightReference.CLAMP_TO_GROUND,
            width: trenchTopWidth + 1, // Slightly wider than cable path for visibility
            material: new ColorMaterialProperty(Color.TRANSPARENT)
        }),
        properties: {
            entityType: EntityTypes.TRENCH_PATH,
            data: {
                startEntity: entityStart.id,
                endEntity: entityEnd.id,
            }
        }
    }));

    // When a path is created, there is always ONE subdivision (delstrekning) when creating a new trench. 
    // Create the cable path entity which is visible above ground. Inherited positions from trenchpath
    const cablePath = trenchPath.entityCollection.add(new Entity({
        id: trenchSubdivisionId,
        parent: trenchPath,
        corridor: new CorridorGraphics({
            positions: polylinePositions,
            cornerType: CornerType.MITERED,
            heightReference: HeightReference.CLAMP_TO_GROUND,
            width: trenchTopWidth,
            material: new ColorMaterialProperty(Color.BEIGE),
            zIndex: 10
        }),
        properties: {
            entityType: EntityTypes.CABLE_PATH,
            data: trenchData
        }
    }));

    // Todo: Create an entity as child which inherits the trenchData. Parent entity should no longer keep trenchData
    return cablePath;
}

/****************** DRAW LINE SHAPE *************************/
const drawLineShape = async ({
    positionData,
    terminate,
    uniqueIDForEntireLine
}: {
    positionData: Cartesian3[] | CallbackProperty;
    terminate: boolean;
    uniqueIDForEntireLine: string;
}) => {

    const {trenchEntityDatasource} = useDataSources();
    const sandboxPolylineEntity = trenchEntityDatasource.entities.getById(
        `cableRoutePolyline:${uniqueIDForEntireLine}`
    );
    if (terminate && sandboxPolylineEntity) {
        const polylinePositions = sandboxPolylineEntity?.polyline?.positions?.getValue(new JulianDate);
        polylinePositions.pop(); // Remove the last point (active shape point which is not part of the polyline)
        if (polylinePositions && polylinePositions.length) {
            const cablePath = await drawTrench(polylinePositions);
            trenchEntityDatasource.entities.remove(sandboxPolylineEntity);
            return cablePath;
        }

    }

    const polylineMaterial =
        get(mapToolsStore).drawPolylineVolume.isHighVoltage
            ? Color.RED
            : Color.BLUE;

    // Update polyline with new position data.
    const newEntityVisualization = trenchEntityDatasource.entities.add({
        id: `cableRoutePolyline:${uniqueIDForEntireLine}`,
        properties: new PropertyBag({
            entityType: EntityTypes.SANDBOX_POLYLINE,
            lineId: uniqueIDForEntireLine
        }),
        polyline: new PolylineGraphics({
            positions: positionData,
            material: new PolylineDashMaterialProperty({
                color: polylineMaterial,
                dashLength: 12
              }),
            clampToGround: true,
            width: 4
        })
    });

    return newEntityVisualization;
}

const terminateShape = async (
        uniqueIDForEntireLine: string
    ) => {
    const {trenchEntityDatasource} = useDataSources();
    //activeShapePoints.pop();
    
    finalEntity = await drawLineShape({ positionData: activeShapePoints, terminate: true, uniqueIDForEntireLine });
    trenchEntityDatasource.entities.remove(floatingPoint as Entity);
    //sandbox_PolylineDataSource.entities.remove(activeShape as Entity);
    floatingPoint = undefined;
    activeShape = undefined;
    activeShapePoints = [];
    // Brannslukking - remove all polyline points and temp entities from datasource
    trenchEntityDatasource.entities.values.forEach((entity) => {
        if(entity.id.startsWith('sandbox_polyline_point:') || entity.id.startsWith('cableRoutePolyline:')){
            trenchEntityDatasource.entities.remove(entity);
        }
    })
    setSelectedEntity(finalEntity);
};


const activateDrawPolylineVolume = (screenSpaceEventHandler: ScreenSpaceEventHandler) => {
    const viewer = useCesiumViewer();
    const scene = viewer.scene;
	const { trenchEntityDatasource } = useDataSources();

    // Enable requestRenderMode to only render when needed. Improves performance
    // This is the start of using this technique to observe potential performance gains
    scene.requestRenderMode = true;

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
    
	if (!viewer.dataSources.contains(trenchEntityDatasource)) {
		viewer.dataSources.add(trenchEntityDatasource);
	}
    viewer.dataSources.raiseToTop(trenchEntityDatasource);
	mapToolsStore.disableAllToolsExceptToggleToolByName('drawPolylineVolume', viewer);

	// Deselect any potentially selected entity at the time of enabling the line drawing tool
	viewer.selectedEntity = undefined;
	// Implementation heavily based on https://sandcastle.cesium.com/index.html?src=Drawing%20on%20Terrain.html

	const uniqueIDForEntireLine = uuidv4();

    viewer.scene.screenSpaceCameraController.enableTranslate = false;
    viewer.scene.screenSpaceCameraController.enableLook = false;

    disableDefaultLeftClickHandler(screenSpaceEventHandler);

    confirmModal(
        'Tegning av grøftetrasé er aktivert. Dette er en eksperimentell versjon, og det vil dermed kunne forekomme feil og mangler. <br /> <br /> <strong>Viktig informasjon</strong><br /> Grøfter må lagres ved å velge grøfteikonet fra sidemenyen og deretter "Lagre grøfter". Om dette ikke gjøres, vil grøftene forsvinne neste gang du laster inn SmartGIS.', 
        () => {},
        () => {},
        {
            labels: {
                title: 'Informasjon om grøfteverktøy',
                cancel: 'Lukk',
                confirm: 'Bekreft'
            }
        }
    )

    /****************** MOUSE MOVE *************************/
    screenSpaceEventHandler.setInputAction((event: { endPosition: Cartesian2 }) => {
        viewer.scene.requestRenderMode = true;
        lastMouse = event.endPosition;
        if (rafId !== null) return;
        rafId = requestAnimationFrame(doHoverWork);
    }, ScreenSpaceEventType.MOUSE_MOVE);
    

    /****************** RIGHT CLICK *************************/
    screenSpaceEventHandler.setInputAction(async () => {
        await terminateShape(uniqueIDForEntireLine);
        deactivateDrawPolylineVolume();
    }, ScreenSpaceEventType.RIGHT_CLICK);


    /****************** LEFT CLICK *************************/
    screenSpaceEventHandler.setInputAction( async (event: { position: Cartesian2 }) => {
        let earthPosition: Cartesian3 | undefined;
        const pickedObject = scene.pick(event.position);
        const entity: Entity = pickedObject?.id;
        const {entityStart} = get(mapToolsStore).drawPolylineVolume;


        // The user haven't started drawing a polyline. Make sure an entity is picked or display an error message
        if(!activeShapePoints.length){
            if (!entityStart && defined(pickedObject)) {
                const entityId = entity.id;
                if(isAllowedTarget(entity)){
                    mapToolsStore.update(store => {
                        store.drawPolylineVolume.entityStart = entity;
                        return store;
                    });
                    if(entity.model){
                        entity.model.silhouetteColor = new ConstantProperty(Color.ORANGE);
                        entity.model.silhouetteSize = new ConstantProperty(2);
                    }
                    if(entity.cylinder){
                        // Is Pole
                        entity.cylinder.outline = new ConstantProperty(true);
                        entity.cylinder.outlineWidth = new ConstantProperty(2);
                        entity.cylinder.outlineColor = new ConstantProperty(Color.ORANGE)
                    } if(entity.box){
                        entity.box.outline = new ConstantProperty(true);
                        entity.box.outlineWidth = new ConstantProperty(2);
                        entity.box.outlineColor = new ConstantProperty(Color.ORANGE);
                    }
                }

                // Sample the ground beneath the entity
                const entityCartesian = entity.position?.getValue(new JulianDate) as Cartesian3;
                const entityPosition = cartesianToLatitudeLongitude(entityCartesian);
                const position = Cartographic.fromDegrees(entityPosition.longitude, entityPosition.latitude);
                const sampledTerrain =  await sampleTerrainMostDetailed(viewer.scene.terrainProvider, [position]);
                earthPosition = Cartesian3.fromDegrees(entityPosition.longitude, entityPosition.latitude, sampledTerrain[0].height);

            } else if (!entityStart && !defined(pickedObject)){
                alert('Du må velge et kabelskap eller en nettstasjon som startpunkt for traséen.');
                return;
            } else {
                console.error('Entity is stored without an activeShape??')
            }
        } else {
            // The user has started drawing a shape. If not intersecting with a valid entity, keep drawing polyline points
            if (entityStart && defined(pickedObject)) {
                const entityId = entity.id;
                if(entity.model){
                    entity.model.silhouetteColor = new ConstantProperty(Color.ORANGE);
                    entity.model.silhouetteSize = new ConstantProperty(2);
                }
                if(entity.cylinder){
                    // Is pole
                    entity.cylinder.outline = new ConstantProperty(true);
                    entity.cylinder.outlineWidth = new ConstantProperty(2);
                    entity.cylinder.outlineColor = new ConstantProperty(Color.ORANGE)
                }
                if(entityId.includes('nettstasjon') || entityId.includes('kabelskap') || entityId.includes(ENTITY_ID_PREFIX.POLE) || entityId.includes('sandbox_box:')){
                    if(entity.cylinder){
                        // Overwrite earthposition to match entity ground position
                        const entityCartesian = entity.position?.getValue(new JulianDate) as Cartesian3;
                        const entityPosition = cartesianToLatitudeLongitude(entityCartesian);
                        const position = Cartographic.fromDegrees(entityPosition.longitude, entityPosition.latitude);
                        const sampledTerrain =  await sampleTerrainMostDetailed(viewer.scene.terrainProvider, [position]);
                        earthPosition = Cartesian3.fromDegrees(entityPosition.longitude, entityPosition.latitude, sampledTerrain[0].height);
                    }

                    confirmModal(
                        'Du har valgt et sluttpunkt (Kabelskap/nettstasjon/mastepunkt). Vil du avslutte traséen?',
                        async () => {
                            mapToolsStore.update(store => {
                                store.drawPolylineVolume.entityEnd = entity;
                                return store;
                            });
                            
                            // Entity end is selected. Finish drawing and deactivate tool
                            const entitiesToUnmark = [entityStart, entity];
                            entitiesToUnmark.forEach((entity) => {
                                if(entity.model){
                                    entity.model.silhouetteColor = undefined;
                                    entity.model.silhouetteSize = new ConstantProperty(0);
                                }
                                if(entity.box){
                                    entity.box.outline = new ConstantProperty(false);
                                    entity.box.outlineWidth = new ConstantProperty(0);
                                    entity.box.outlineColor = undefined
                                }
                                if(entity.cylinder){
                                    entity.cylinder.outline = new ConstantProperty(false);
                                    entity.cylinder.outlineWidth = new ConstantProperty(0);
                                    entity.cylinder.outlineColor = undefined
                                }
                            })
                            await terminateShape(uniqueIDForEntireLine);
                            deactivateDrawPolylineVolume();
                        },
                        () => { return false }
                    )
                }
            }
        }

        if (typeof earthPosition === 'undefined' || !earthPosition) {
            earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
        }

        // `earthPosition` will be undefined if our mouse is not over the globe.
        if (earthPosition && defined(earthPosition)) {
            if (activeShapePoints.length === 0) {
                floatingPoint = createPolylinePoint(earthPosition, uniqueIDForEntireLine);
                activeShapePoints.push(earthPosition);
                const dynamicPositions = new CallbackProperty(() => activeShapePoints, false);
                activeShape = await drawLineShape({ positionData: dynamicPositions, terminate: false, uniqueIDForEntireLine });
            }
            activeShapePoints.push(earthPosition);
            earthPosition = undefined;
        }
    }, ScreenSpaceEventType.LEFT_CLICK);
}


const deactivateDrawPolylineVolume = (): void => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
	mapToolsStore.disableAllToolsExceptToggleToolByName('drawPolylineVolume', viewer);
    // If the user disables the "add pole" tool, remove the input action event listener
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

    screenSpaceEventHandler.setInputAction(
        OriginalCesiumInputActions.LEFT_CLICK,
        ScreenSpaceEventType.LEFT_CLICK
    );
    
    
    // Reset tool
    activeShapePoints = [];
    activeShape = undefined;
    floatingPoint = undefined;

    if (lastHighlighted && lastHighlighted.cylinder && lastOriginal) {
        const cyl = lastHighlighted.cylinder;
        cyl.material = lastOriginal.material;
        cyl.outline = lastOriginal.outline;
        cyl.outlineColor = lastOriginal.outlineColor;
        (cyl as any).outlineWidth = lastOriginal.outlineWidth; // width er property/number avh. av oppsett
    }
    // Reset highlights
    lastHighlighted = null;
    lastOriginal = null;
    rafId = null;
    lastMouse = null;

    viewer.scene.requestRenderMode = false;
    viewer.scene.screenSpaceCameraController.enableTranslate = true;
    viewer.scene.screenSpaceCameraController.enableLook = true;

    if(finalEntity){
        setSelectedEntity(finalEntity);
        finalEntity = undefined;
    }
};

export {
    activateDrawPolylineVolume,
    deactivateDrawPolylineVolume
}